<template>
	<div class="ventas_tab report_theme_container" data-theme="ventas">
		<TabHeader
			title="Ventas"
			description="Analiza la tendencia y composición de tus ventas"
			:filterPeriod="filterPeriod"
			:filterOptions="visiblePeriodOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			calendarMode="range"
			:dateRange="calendarRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange">
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button v-for="filter in subNavFilters" :key="filter.id" class="filter_btn"
						:class="{ 'is-active': activeSubFilter === filter.id }"
						@click="updateActiveSubFilter(filter.id)">
						{{ filter.label }}
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- Gráficos del header: stacked bar por cliente + actividad (count
			de clientes + ticket promedio). Colapsable para liberar alto cuando
			el foco está en la tabla; estado persiste en localStorage por tab.
			Key nueva (no la histórica de summary) para no arrastrar la
			preferencia de un layout distinto. -->
		<CollapsibleSection class="mt-1"
			:title="chartsSectionTitle"
			chevron-right
			heading
			storageKey="financialReports:charts-expanded">
			<SalesChartsRow
				:chartsData="chartsData"
				:isLoading="isChartsLoading"
				:error="chartsError" />
		</CollapsibleSection>

		<!-- Main Table Section -->
		<SalesEvolutionTable class="mt-2"
			:monthHeaders="monthHeaders"
			:tableRows="tableRows"
			:totalRow="totalRow"
			:isLoading="isLoading"
			:error="error"
			:filterPeriod="filterPeriod"
			:comparison-enabled="activeSubFilter !== 'clientes'"
			:comparison="comparisonViewModel"
			@retry="fetchSalesData"
			@cell-click="openSalesAnalysis"
			@toggle-comparison="onToggleComparison"
			@comparison-select-item="onComparisonSelectItem"
			@comparison-add-year="onComparisonAddYear"
			@comparison-remove-year="onComparisonRemoveYear" />
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import SalesChartsRow from "@/components/financialReports/SalesChartsRow.vue";
import SalesEvolutionTable from "@/components/financialReports/SalesEvolutionTable.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderSalesAnalysis from "@/components/financialReports/salesAnalysis/HeaderSalesAnalysis.vue";
import BodySalesAnalysis from "@/components/financialReports/salesAnalysis/BodySalesAnalysis.vue";
import HeaderClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/HeaderClientDetail.vue";
import BodyClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/BodyClientDetail.vue";
import {
    FILTER_PERIOD_OPTIONS,
    TOP_CLIENTS_COUNT,
} from "../config/reportStructure";
import { parseMonthLabel } from "../utils/periodFormatting";
import { capitalize } from "@/utils/string";

export default {
	name: "VentasTab",

	components: {
		TabHeader,
		CollapsibleSection,
		SalesChartsRow,
		SalesEvolutionTable,
	},

	data() {
		return {
			filterOptions: FILTER_PERIOD_OPTIONS,
			// Panel de comparación abierto (toggle del botón de la tabla). Lo
			// trackeamos acá para refetchear la comparación al cambiar el mes
			// del header sólo si el panel está visible.
			comparisonOpen: false,
			// ID del setTimeout del debounce de category-change. Inicializado
			// en data() (en lugar de como propiedad `this._xxx` ad-hoc) para
			// que sea forward-compat con Vue 3 — el Proxy intercepta toda
			// asignación a `this`, así que el truco del prefijo "_" para
			// evadir el observer de Vue 2 deja de aplicar. Hacerlo reactive
			// es inofensivo (es un número, sin template binding).
			categoryChangeTimer: null,
		};
	},

	computed: {
		...mapState("classification", ["categories", "itemsByCategory"]),
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange", "customRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("salesReport", [
			"activeSubFilter",
			"monthHeaders",
			"isLoading",
			"error",
			"chartsData",
			"isChartsLoading",
			"chartsError",
			"comparisonData",
			"isComparisonLoading",
			"comparisonError",
			"comparisonYears",
			"comparisonItem",
		]),
		// Lente activo del drawer — el header del modal puede cambiar el mes y
		// necesitamos retener account/groupBy/selectedItems para reusarlos en
		// el refetch sin leer state.x directo desde un handler.
		...mapState("salesAnalysis", {
			analysisAccount: "account",
			analysisGroupBy: "groupBy",
			analysisSelectedItems: "selectedItems",
			analysisMonth: "month",
		}),
		...mapGetters("salesReport", ["tableRows", "totalRow"]),

		getNow() {
			return moment().toDate();
		},

		// Rango activo del calendar del header. Si el store tiene `customRange`
		// poblado lo usamos tal cual; si no, derivamos un default razonable a
		// partir de endMonth + selectedPeriodRange para que el calendar abra
		// pintando "Último año" en lugar de "—" / "—". Ese default es sólo
		// visual: el fetch sigue usando `selectedPeriodRange` hasta que el
		// usuario interactúe con el calendar y dispare `updateDateRange`.
		calendarRange() {
			if (this.customRange) return this.customRange;
			const end = moment(this.endMonth).startOf("month");
			return { from: end.clone().subtract(11, "months").toDate(), to: end.toDate() };
		},

		// Períodos visibles en el header. En modo comparación YTD/Anual no
		// aplican: el gráfico compara curvas mensuales/trimestrales año-contra-
		// año, así que sólo dejamos Mensual y Trimestral mientras el panel está
		// abierto. Con la tabla visible se muestran los cuatro.
		visiblePeriodOptions() {
			if (!this.comparisonOpen) return this.filterOptions;
			return this.filterOptions.filter(
				(o) => o.id === "month" || o.id === "quarter"
			);
		},

		subNavFilters() {
			const filters = [{ id: "cuentas", label: "Cuentas" }];
			this.categories.forEach(category => {
				filters.push({ id: category, label: capitalize(category) });
			});
			filters.push({ id: "clientes", label: "Clientes" });
			return filters;
		},

		// Título de la sección de gráficos, dinámico según el sub-filtro activo:
		// "Ventas por cuenta" / "Ventas por cliente" / "Ventas por <categoría>".
		// Tomamos el label del chip activo y lo singularizamos en minúscula —
		// los labels del dominio vienen en plural ("Cuentas", "Clientes",
		// "Tiendas"), así que quitar la "s" final cubre los casos reales.
		chartsSectionTitle() {
			const active = this.subNavFilters.find(
				(f) => f.id === this.activeSubFilter
			);
			const label = active ? active.label : "";
			const singular = label.replace(/s$/i, "").toLowerCase();
			return `Ventas por ${singular}`;
		},

		// Categorías dinámicas del tab (tiendas, marcas, …) sin las pseudo-
		// categorías cuentas/clientes — esas ya tienen UI propia en otra parte
		// (sub-nav del tab y card del body). Cada entrada va a un
		// SelectComponent en el header del drawer. Las options vienen del
		// classification store (itemsByCategory), poblado por la action
		// `classification/getCategories` que disparamos en `mounted` (short-
		// circuit si ya está cargado).
		headerCategories() {
			return this.subNavFilters
				.filter((f) => f.id !== "cuentas" && f.id !== "clientes")
				.map((f) => ({
					id: f.id,
					label: f.label,
					options: (this.itemsByCategory[f.id] || []).map((name) => ({
						value: name,
						label: name,
					})),
				}));
		},

		// Lista de categorías para la card de Distribución del modal de
		// análisis — backend soporta múltiples (max 2 hoy: tiendas + marcas
		// típicamente) y devuelve una distribución por cada una. Sin
		// categorías configuradas, el card del frontend skipea su render.
		analysisGroupByForOpen() {
			return Array.isArray(this.categories) ? this.categories.slice(0, 2) : [];
		},

		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción. Incluye `customRange`
		// para que cambiar el rango via el calendar (que también setea
		// selectedPeriodRange="custom") refetchee aunque endMonth ya sea el
		// mismo.
		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.activeSubFilter,
				this.filterPeriod,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
			].join("|");
		},

		// Los gráficos reflejan el sub-filtro activo (por cuenta / categoría /
		// cliente), así que `activeSubFilter` entra en la signature para
		// refetchear al cambiar de modo.
		chartsSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.activeSubFilter,
				this.filterPeriod,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
			].join("|");
		},

		// True cuando el sub-filtro activo es una categoría (tiendas/marcas) —
		// ni cuentas ni clientes. Sólo en ese caso la comparación filtra por
		// subcategoría.
		isCategoryFilter() {
			return this.activeSubFilter !== "cuentas"
				&& this.activeSubFilter !== "clientes";
		},

		// Subcategorías de la categoría activa (botones del gráfico de
		// comparación). Vacío en cuentas/clientes.
		comparisonSubItems() {
			return this.isCategoryFilter
				? (this.itemsByCategory[this.activeSubFilter] || [])
				: [];
		},

		// View-model que se pasa entero a SalesEvolutionTable → SalesComparisonChart
		// (sus keys matchean 1:1 las props del chart).
		comparisonViewModel() {
			return {
				series: this.comparisonData ? this.comparisonData.series : [],
				months: this.comparisonData ? this.comparisonData.months : [],
				availableYears: this.comparisonData
					? this.comparisonData.availableYears
					: [],
				isLoading: this.isComparisonLoading,
				error: this.comparisonError || "",
				subItems: this.comparisonSubItems,
				selectedItem: this.comparisonItem,
				years: this.comparisonYears,
				// Granularidad del eje X. El backend siempre manda 12 meses; el
				// chart agrega a quarters cuando el período activo es trimestral.
				period: this.filterPeriod,
			};
		},
	},

	methods: {
		...mapActions("salesReport", ["initVentas", "fetchSalesData", "fetchSalesCharts", "fetchSalesComparison"]),
		...mapActions("reportsFilters", ["setCustomRange"]),
		...mapActions("salesAnalysis", ["fetchSalesAnalysis"]),
		// Reuso del módulo clientDetail (mismo drawer que abre BodySalesAnalysis
		// cuando se clickea un cliente del ranking). Acá lo necesitamos para el
		// short-circuit: en modo "clientes" + L3, saltamos el primer modal y
		// abrimos directo el detalle del cliente.
		...mapActions("clientDetail", {
			openClientDetailAction: "openClientDetail",
			closeClientDetailAction: "closeClientDetail",
			changeClientRangePeriod: "changeRangePeriod",
		}),
		// Categorías + items son globales (no específicos de Ventas); el
		// store hace short-circuit si ya están cargados por otro view.
		...mapActions("classification", ["getCategories"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("salesReport", [
			"SET_ACTIVE_SUB_FILTER",
			"SET_COMPARISON_ITEM",
			"SET_COMPARISON_YEARS",
		]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),
		...mapMutations("salesAnalysis", {
			resetSalesAnalysis: "RESET",
			setSelectedItems: "SET_SELECTED_ITEMS",
		}),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateDateRange(range) {
			// Dispatch a la action — la acción se encarga de coordinar el set
			// del rango + sincronizar `selectedPeriodRange = "custom"` + endMonth
			// para que cualquier consumer que mire esas keys aisladas (legacy)
			// siga coherente con el rango actual.
			this.setCustomRange(range);
		},
		updateActiveSubFilter(filter) {
			this.SET_ACTIVE_SUB_FILTER(filter);
			// La comparación depende del modo: en categoría arranca en la
			// primera subcategoría; en cuentas/clientes no aplica subcategoría.
			const isCategory = filter !== "cuentas" && filter !== "clientes";
			const items = isCategory ? (this.itemsByCategory[filter] || []) : [];
			this.SET_COMPARISON_ITEM(isCategory && items.length ? items[0] : null);
			if (this.comparisonOpen) this.fetchSalesComparison();
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},

		// --- Gráfico de comparación año-contra-año ---
		// Toggle del panel: al abrir fija la subcategoría default (modo
		// categoría) y dispara el fetch lazy. La tabla mantiene su propio
		// estado de apertura; acá sólo lo espejamos para el watcher de mes.
		onToggleComparison(open) {
			this.comparisonOpen = open;
			if (!open) return;
			// YTD/Anual no aplican a la comparación. Si el período activo es uno
			// de esos al abrir el panel, caemos a Mensual para que el gráfico y
			// los chips visibles arranquen en una granularidad válida.
			if (this.filterPeriod !== "month" && this.filterPeriod !== "quarter") {
				this.SET_FILTER_PERIOD("month");
			}
			if (this.isCategoryFilter && !this.comparisonItem
				&& this.comparisonSubItems.length) {
				this.SET_COMPARISON_ITEM(this.comparisonSubItems[0]);
			}
			this.fetchSalesComparison();
		},
		onComparisonSelectItem(item) {
			this.SET_COMPARISON_ITEM(item);
			this.fetchSalesComparison();
		},
		onComparisonAddYear(year) {
			this.SET_COMPARISON_YEARS([...this.comparisonYears, year]);
			this.fetchSalesComparison();
		},
		onComparisonRemoveYear(year) {
			this.SET_COMPARISON_YEARS(
				this.comparisonYears.filter((y) => y !== year)
			);
			this.fetchSalesComparison();
		},

		// Refetch del análisis cuando el usuario cambia el mes desde el
		// AnualCalendar del header del drawer. account/groupBy/selectedItems
		// se conservan (vía mapState) — el drawer sigue contextualizado al
		// mismo lente, sólo cambia el período.
		onAnalysisMonthChange(date) {
			const month = moment(date).format("YYYY-MM");
			this.fetchSalesAnalysis({
				month,
				account: this.analysisAccount,
				groupBy: this.analysisGroupBy,
				selectedItems: this.analysisSelectedItems,
			});
		},

		// Cambio de filtro multi-select en un SelectComponent del header.
		// 1) Merge inmediato al store para que el header refleje el estado
		//    sin esperar al fetch.
		// 2) Debounce del refetch — varios checkbox toggles seguidos quedan
		//    colapsados en un solo request.
		onCategoryChange({ id, values }) {
			const merged = { ...this.analysisSelectedItems, [id]: values };
			this.setSelectedItems(merged);
			clearTimeout(this.categoryChangeTimer);
			this.categoryChangeTimer = setTimeout(() => {
				this.fetchSalesAnalysis({
					month: this.analysisMonth,
					account: this.analysisAccount,
					groupBy: this.analysisGroupBy,
					selectedItems: merged,
				});
			}, 600);
		},

		// Wrap del cleanup del store + cancelación del timer pendiente —
		// si el user cierra el drawer mientras hay un fetch debounced, evita
		// dispatchear contra un store ya reseteado.
		onAnalysisClose() {
			clearTimeout(this.categoryChangeTimer);
			this.categoryChangeTimer = null;
			this.resetSalesAnalysis();
		},

		// Atajo del modo "clientes" + L3: abre el drawer de detalle del cliente
		// directo (mismo que el segundo modal del flujo de análisis). Le
		// pasamos el monthIso del mes clickeado y el alias del cliente; la
		// action `clientDetail/openClientDetail` resuelve el `ciId` con
		// `resolve_counterpart_by_alias`.
		openClientDetailFromCell({ client, month }) {
			this.openClientDetailAction({
				alias: client,
				documentType: "C",
				monthIso: month,
				obligationName: "Otros",
				level2Name: "Ventas",
			});

			this.$buefy.modal.open({
				parent: this,
				hasModalCard: true,
				customClass: "wide-summary-modal",
				canCancel: ["escape", "outside"],
				component: ModalFullHeightRight,
				props: {
					headerComp: HeaderClientDetail,
					bodyComp: BodyClientDetail,
					// fallbackAlias: si la resolución del counterpart falla, el
					// header sigue mostrando el nombre que vimos en la tabla
					// (en lugar de quedar vacío). El header ya consume
					// `state.counterpart.alias` cuando llega.
					headerProps: { fallbackAlias: client },
					bodyProps: null,
					// Theme propagado para que el subárbol del drawer use los
					// tokens del tab Ventas (modal vive en document.body, no
					// hereda el data-theme del .ventas_tab).
					theme: "ventas",
					// Width default (85%) — es el primer drawer en este flujo,
					// no se apila sobre otro. BodySalesAnalysis usa 70% para
					// stackeo; acá no aplica.
				},
				events: {
					closed: this.closeClientDetailAction,
					"update-range-period": this.changeClientRangePeriod,
				},
			});
		},

		openSalesAnalysis({ monthLabel, account }) {
			const month = parseMonthLabel(monthLabel);
			if (!month) return;

			// Atajo del modo "clientes": la tabla es una lista plana de clientes,
			// así que cualquier celda con `account` ES un cliente. El análisis
			// del mes (ranking de clientes, distribución de tiendas) sería ruido
			// — el usuario ya identificó el cliente. Saltamos el primer drawer y
			// abrimos directo el detalle del cliente. La fila total (account null)
			// no entra acá y cae al análisis general.
			const sub = this.activeSubFilter;
			if (sub === "clientes" && account) {
				this.openClientDetailFromCell({ client: account, month });
				return;
			}

			// Resto de los casos (cuentas, categorías, total row):
			// el modal se abre como si fuera la celda de "Ventas Netas" de la
			// misma columna — sin drillAccount ni preselección de attribute. El
			// usuario decide después dentro del drawer si quiere acotar a una
			// cuenta puntual via el header. La razón: el card de ranking de
			// clientes y la distribución por categoría ya muestran ese
			// breakdown — pre-filtrar a la cuenta/atributo clickeado fuerza un
			// recorte que el usuario quizás no quería.
			const drillAccount = null;
			const drillSelectedItems = {};
			const monthDate = moment(month, "YYYY-MM").toDate();
			// Capitalizado al modo del chip del diseño ("Abr 2025") — fallback
			// para el header si endMonth no se renderea (el caller ya pasa
			// endMonth, así que en práctica el AnualCalendar lo reemplaza).
			const periodLabel = moment(month, "YYYY-MM")
				.locale("es")
				.format("MMM YYYY")
				.replace(/^./, (c) => c.toUpperCase());

			this.fetchSalesAnalysis({
				month,
				account: drillAccount,
				groupBy: this.analysisGroupByForOpen,
				selectedItems: drillSelectedItems,
			});
			this.$buefy.modal.open({
				parent: this,
				hasModalCard: true,
				customClass: "wide-summary-modal",
				canCancel: ["escape", "outside"],
				component: ModalFullHeightRight,
				props: {
					headerComp: HeaderSalesAnalysis,
					bodyComp: BodySalesAnalysis,
					headerProps: {
						periodLabel,
						endMonth: monthDate,
						getNow: this.getNow,
						categories: this.headerCategories,
					},
					bodyProps: null,
					// El modal se monta en document.body — sin esto el subárbol
					// no hereda el [data-theme="ventas"] del .ventas_tab.
					theme: "ventas",
				},
				events: {
					closed: this.onAnalysisClose,
					"update-end-month": this.onAnalysisMonthChange,
					"category-change": this.onCategoryChange,
				},
			});
		},
	},

	mounted() {
		// Bootstrap orquestado por la action — el view declara intent ("init
		// del tab Ventas") y el store decide qué se carga en paralelo. Los
		// watchers de fetchSignature/chartsSignature siguen llamando las
		// actions individuales según corresponda.
		this.initVentas({ topN: TOP_CLIENTS_COUNT });
		// classification/getCategories alimenta `categories` + `itemsByCategory`
		// que `subNavFilters` y `headerCategories` consumen. Si el usuario
		// llegó a Ventas por otra ruta (DashboardClassificator ya las cargó),
		// la action hace short-circuit.
		this.getCategories();
	},

	beforeDestroy() {
		// Si el tab se desmonta con un fetch debounced pendiente, lo cancelamos
		// para evitar que dispare contra un store/component ya fuera de scope.
		clearTimeout(this.categoryChangeTimer);
	},

	watch: {
		fetchSignature() { this.fetchSalesData(); },
		chartsSignature() { this.fetchSalesCharts({ topN: TOP_CLIENTS_COUNT }); },
		// El mes del header redefine el cutoff del año en curso de la
		// comparación — refetch sólo si el panel está abierto (el resto de los
		// disparadores —subcategoría, años, sub-filter— se manejan en sus
		// handlers explícitos para no duplicar requests).
		endMonth() {
			if (this.comparisonOpen) this.fetchSalesComparison();
		},
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.ventas_tab {
	position: relative;
	height: 100%;
}

// El header común vive en TabHeader.vue. Acá solo queda el SCSS de los
// sub-filters dinámicos (Cuentas/Categorías/Clientes) inyectados vía
// el slot #center-sub-nav — re-usan las classes .sub_nav_filters/.filter_btn
// que TabHeader expone con scoped styles propios, así que los repetimos
// localmente para que apliquen al markup del slot.

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-surface);
	}

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}
</style>
